import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Collapse, Divider, Typography } from '@mui/material';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import GroupsIcon from '@mui/icons-material/Groups';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CategoryCard from './CategoryCard';
import CategoryDetail from './CategoryDetail';

// ── Status palette ────────────────────────────────────────────────────────────
const STATUS = {
  green:  { color: '#2e7d32', bg: '#e8f5e9', alpha04: 'rgba(46,125,50,0.04)',  label: 'HEALTHY'  },
  yellow: { color: '#f9a825', bg: '#fff8e1', alpha04: 'rgba(249,168,37,0.04)', label: 'WARNING'  },
  red:    { color: '#c62828', bg: '#ffebee', alpha04: 'rgba(198,40,40,0.04)',  label: 'CRITICAL' },
};

// ── Category metadata ─────────────────────────────────────────────────────────
const CATEGORY_LABELS = {
  sales: 'Sales',
  labor: 'Labor',
  tips: 'Tips',
  cogs: 'COGs',
  other: 'Other',
};

const CATEGORY_ORDER = ['sales', 'labor', 'tips', 'cogs', 'other'];

const CATEGORY_ICONS = {
  sales: PointOfSaleIcon,
  labor: GroupsIcon,
  tips: AttachMoneyIcon,
  cogs: RestaurantIcon,
  other: MoreHorizIcon,
};

const CATEGORY_HEX = {
  sales: '#1976d2',
  labor: '#7b1fa2',
  tips: '#2e7d32',
  cogs: '#e65100',
  other: '#0288d1',
};

const fmt = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const Dashboard = ({
  firebaseUser,
  stores,
  startDate,
  endDate,
  selectedStore,
  loadKey,
}) => {
  const [categoryResults, setCategoryResults] = useState({});
  const [categoryLoading, setCategoryLoading] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('sales');
  const [sourceHealth, setSourceHealth] = useState({});

  const isPlaceholder = loadKey === 0;

  // ── Per-category parallel loading ──────────────────────────────────────────
  useEffect(() => {
    if (!firebaseUser?.getIdToken || !startDate || !endDate || loadKey === 0) return;

    let cancelled = false;

    // Mark all categories as loading
    const loading = {};
    CATEGORY_ORDER.forEach((k) => { loading[k] = true; });
    setCategoryLoading(loading);

    const fetchCategory = async (categoryKey) => {
      try {
        const token = await firebaseUser.getIdToken(true);
        const params = new URLSearchParams({
          startDate: fmt(startDate),
          endDate: fmt(endDate),
          storeId: selectedStore,
          category: categoryKey,
        });
        const res = await axios.get(
          `/hd-api/data-integrity-2/summary?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) {
          setCategoryResults((prev) => ({
            ...prev,
            [categoryKey]: res.data.results[categoryKey] || [],
          }));
          setCategoryLoading((prev) => ({ ...prev, [categoryKey]: false }));
        }
      } catch {
        if (!cancelled) {
          setCategoryLoading((prev) => ({ ...prev, [categoryKey]: false }));
        }
      }
    };

    // Fire all 5 in parallel
    CATEGORY_ORDER.forEach(fetchCategory);

    return () => { cancelled = true; };
  }, [loadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Source health (freshness + trend) ──────────────────────────────────────
  useEffect(() => {
    if (!firebaseUser?.getIdToken || !endDate) return;
    let cancelled = false;
    const fetchHealth = async () => {
      try {
        const token = await firebaseUser.getIdToken(true);
        const params = new URLSearchParams({ endDate: fmt(endDate) });
        const res = await axios.get(`/hd-api/data-integrity-2/sources/health?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setSourceHealth(res.data.health || {});
      } catch {
        // non-critical — columns will show dashes
      }
    };
    fetchHealth();
    return () => { cancelled = true; };
  }, [firebaseUser, endDate]);

  const handleCardClick = (categoryKey) => {
    setSelectedCategory((prev) => (prev === categoryKey ? null : categoryKey));
  };

  const selectedSources = selectedCategory ? (categoryResults[selectedCategory] || []) : [];

  return (
    <Box>
      {/* Category Cards Row */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {CATEGORY_ORDER.map((categoryKey) => {
          const sources = categoryResults[categoryKey] || [];
          return (
            <CategoryCard
              key={categoryKey}
              categoryKey={categoryKey}
              categoryLabel={CATEGORY_LABELS[categoryKey]}
              categoryHex={CATEGORY_HEX[categoryKey]}
              CategoryIcon={CATEGORY_ICONS[categoryKey]}
              sources={sources}
              status={STATUS}
              isSelected={selectedCategory === categoryKey}
              onClick={() => handleCardClick(categoryKey)}
              loading={!!categoryLoading[categoryKey]}
            />
          );
        })}
      </Box>

      {/* Placeholder notice anchored to divider */}
      {isPlaceholder && (
        <Divider sx={{ mt: 2, mb: 0 }}>
          <Typography
            variant="caption"
            sx={{ color: 'text.disabled', fontStyle: 'italic' }}
          >
            Click Load to fetch live results
          </Typography>
        </Divider>
      )}

      {/* Detail Panel — animated open/close */}
      <Collapse in={!!selectedCategory && !isPlaceholder} timeout={250} unmountOnExit>
        {selectedCategory && (
          <CategoryDetail
            key={selectedCategory}
            categoryKey={selectedCategory}
            categoryLabel={CATEGORY_LABELS[selectedCategory]}
            categoryHex={CATEGORY_HEX[selectedCategory]}
            CategoryIcon={CATEGORY_ICONS[selectedCategory]}
            sources={selectedSources}
            status={STATUS}
            sourceHealth={sourceHealth}
            firebaseUser={firebaseUser}
            stores={stores}
            startDate={startDate}
            endDate={endDate}
            selectedStore={selectedStore}
            isPlaceholder={isPlaceholder}
          />
        )}
      </Collapse>
    </Box>
  );
};

export default Dashboard;
